import { useState } from 'react'

import { ArticleApprovalListItem } from '@/common/api/params/articleApproval'
import {
  ListFollowTableHeaderProps,
  makeRectPropsForTableHeadItem,
  useListScrollFollow
} from '@/common/app/list'
import {
  articleApprovalStatusBadgeType,
  articleApprovalStatusLabel
} from '@/common/app/modules/media/articleApproval'
import { articleApprovalDetailPath } from '@/common/app/path'
import { dateTimeStr } from '@/common/form/date'

import Badge from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import IconButton from '@/components/view/atoms/button/IconButton'
import { IconType } from '@/components/view/atoms/icon/Icon'
import ClickableTableRow from '@/components/view/atoms/table/ClickableTableRow'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import DisclosureWrapper from '@/components/view/layouts/disclosure/DisclosureWrapper'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import InternalLink from '@/components/view/utils/link/InternalLink'

import ArticleApprovalScheduleEdit from '@/components/container/modules/media/article/ArticleApprovalScheduleEdit'
import ScrollFollowTable from '@/components/container/utils/list/ScrollFollowTable'

const HEADERS = [
  'リクエストID',
  'ページ',
  '版',
  'ステータス',
  '承認状況',
  'アップロード予定日時',
  '依頼者',
  '操作'
]
const COLUMN_LENGTH = HEADERS.length

interface TheadProps extends ListFollowTableHeaderProps {
  float?: boolean
}

const Thead = ({ rowRefs, rowRects, float = false }: TheadProps) => {
  const rectProps = (index: number) =>
    makeRectPropsForTableHeadItem(rowRefs, rowRects, index, float)
  return (
    <>
      {HEADERS.map((h, i) => (
        <TableHeadItem key={i} float={float} {...rectProps(i)}>
          <Text size={12} color={TextColor.SECONDARY}>
            {h}
          </Text>
        </TableHeadItem>
      ))}
    </>
  )
}

interface Props {
  siteId: number
  requests: ArticleApprovalListItem[]
  onApprove: (requestId: number) => void
  approvingId?: number | null
  onScheduleChanged?: () => void
}

// アップロード予定日時を変更できるステータス（承認詳細画面と同基準）
const canEditSchedule = (status: string) => status === 'pending' || status === 'upload_ready'

const ArticleApprovalTable = ({
  siteId,
  requests,
  onApprove,
  approvingId,
  onScheduleChanged
}: Props) => {
  const listFollowProps = useListScrollFollow(COLUMN_LENGTH)
  const { header } = listFollowProps
  const condition = { page: 1, perPage: Math.max(requests.length, 1) }
  // 予定日時の変更 Disclosure を開いているリクエストID
  const [editingId, setEditingId] = useState<number | null>(null)

  return (
    <ScrollFollowTable
      listFollowProps={listFollowProps}
      tableHead={<Thead {...header} />}
      tableFloatHead={<Thead {...header} float />}
      condition={condition}
      onUpdatePage={() => {}}
      total={requests.length}
      body={requests.map((r) => (
        <ClickableTableRow key={r.id} href={articleApprovalDetailPath(siteId, r.id)}>
          <TableRowItem width={88}>
            <Text size={12} color={TextColor.SECONDARY}>
              {r.id}
            </Text>
          </TableRowItem>
          <TableRowItem>
            <InternalLink link={articleApprovalDetailPath(siteId, r.id)}>
              <Text weight='bold' color={TextColor.LINK} truncate>
                {r.title || r.page_id || '-'}
              </Text>
            </InternalLink>
          </TableRowItem>
          <TableRowItem width={64}>
            <Text>v{r.version_number}</Text>
          </TableRowItem>
          <TableRowItem width={128}>
            <Badge size={10} badgeType={articleApprovalStatusBadgeType(r.status)}>
              {articleApprovalStatusLabel(r.status)}
            </Badge>
          </TableRowItem>
          <TableRowItem width={88}>
            {r.approved_by_name ? (
              <Text color={TextColor.SUCCESS}>承認: {r.approved_by_name}</Text>
            ) : r.rejected_by_name ? (
              <Text color={TextColor.CRITICAL}>差戻: {r.rejected_by_name}</Text>
            ) : (
              <Text color={TextColor.SECONDARY}>未承認</Text>
            )}
          </TableRowItem>
          <TableRowItem width={160}>
            <ItemAlign align='center'>
              <Text size={12} color={TextColor.SECONDARY}>
                {r.scheduled_upload_at ? dateTimeStr(r.scheduled_upload_at) : '-'}
              </Text>
              {canEditSchedule(r.status) && (
                // 行クリックの詳細遷移を発火させないよう、変更UIはクリック伝播を止める
                <div onClick={(e) => e.stopPropagation()}>
                  <DisclosureWrapper
                    off={editingId !== r.id}
                    onClickOutSide={() => setEditingId(null)}
                    disclosureNode={
                      <ArticleApprovalScheduleEdit
                        requestId={r.id}
                        siteId={siteId}
                        scheduledUploadAt={r.scheduled_upload_at}
                        onChange={() => {
                          onScheduleChanged?.()
                          setEditingId(null)
                        }}
                        onClickCancel={() => setEditingId(null)}
                      />
                    }
                  >
                    <IconButton
                      type={IconType.EDIT}
                      line
                      onClick={() => setEditingId((prev) => (prev === r.id ? null : r.id))}
                    />
                  </DisclosureWrapper>
                </div>
              )}
            </ItemAlign>
          </TableRowItem>
          <TableRowItem width={128}>
            <Text size={12} color={TextColor.SECONDARY} truncate>
              {r.requested_by_name || '-'}
            </Text>
          </TableRowItem>
          <TableRowItem width={128}>
            {r.status === 'pending' ? (
              <Button
                color={ButtonColor.PRIMARY}
                loading={approvingId === r.id}
                onClick={() => onApprove(r.id)}
              >
                承認する
              </Button>
            ) : (
              <Text color={TextColor.SECONDARY}>-</Text>
            )}
          </TableRowItem>
        </ClickableTableRow>
      ))}
    />
  )
}

export default ArticleApprovalTable
